import { ref, computed } from 'vue'
import { useStorage } from '@vueuse/core'

const APP_TYPES = Object.freeze({
  KIDS: 'KIDS',
  GUARDIAN: 'GUARDIAN',
  PETS: 'PETS'
})

const DEFAULT_REDIRECTS = {
  [APP_TYPES.KIDS]: import.meta.env.DEV
    ? 'http://localhost:5200'
    : 'findeekids://findee.com/callback',
  [APP_TYPES.GUARDIAN]: import.meta.env.DEV
    ? 'http://localhost:5201'
    : 'findeeguardian://app.findeeguardian.com/callback',
  [APP_TYPES.PETS]: import.meta.env.DEV
    ? 'http://localhost:5202'
    : 'findeepets://findeepets.com/callback'
}

const REDIRECT_URLS = {
  [APP_TYPES.KIDS]:
    import.meta.env.VITE_APP_REDIRECT_URL_KIDS ||
    import.meta.env.VITE_APP_REDIRECT_URL ||
    DEFAULT_REDIRECTS[APP_TYPES.KIDS],
  [APP_TYPES.GUARDIAN]:
    import.meta.env.VITE_APP_REDIRECT_URL_GUARDIAN || DEFAULT_REDIRECTS[APP_TYPES.GUARDIAN],
  [APP_TYPES.PETS]: import.meta.env.VITE_APP_REDIRECT_URL_PETS || DEFAULT_REDIRECTS[APP_TYPES.PETS]
}

const DEFAULT_APP_TYPE = APP_TYPES.KIDS
const APP_TYPE_QUERY_PARAM = 'APP'
const APP_TYPE_ALIASES = Object.freeze({
  KIDS: APP_TYPES.KIDS,
  FINDEEKIDS: APP_TYPES.KIDS,
  GUARDIAN: APP_TYPES.GUARDIAN,
  FINDEEGUARDIAN: APP_TYPES.GUARDIAN,
  PETS: APP_TYPES.PETS,
  FINDEEPETS: APP_TYPES.PETS
})

const ENV_PREFIX =
  import.meta.env.VITE_APP_STORAGE_NAMESPACE || (import.meta.env.PROD ? 'PROD' : 'DEV')
const APP_TYPE_STORAGE_KEY = `FINDEE_AUTH_${ENV_PREFIX}_appType`

// x-app-code
const APPS_HEADERS = {
  [APP_TYPES.KIDS]: 'FINDEE-KIDS',
  [APP_TYPES.GUARDIAN]: 'FINDEE-GUARDIAN',
  [APP_TYPES.PETS]: 'FINDEE-PETS'
}

const normalizeAppType = (type) => {
  const rawType = Array.isArray(type) ? type.find((value) => String(value || '').trim()) : type
  const normalizedTypeKey = String(rawType || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')

  return APP_TYPE_ALIASES[normalizedTypeKey] || null
}

const resolveAppType = (type) => normalizeAppType(type) || DEFAULT_APP_TYPE

const getAppTypeQueryValue = (query = {}) => {
  const queryEntries = Object.entries(query)
  const upperCaseEntry = queryEntries.find(([key]) => key === APP_TYPE_QUERY_PARAM)
  if (upperCaseEntry) return upperCaseEntry[1]

  const lowerCaseEntry = queryEntries.find(([key]) => key === APP_TYPE_QUERY_PARAM.toLowerCase())
  if (lowerCaseEntry) return lowerCaseEntry[1]

  const matchingEntry = queryEntries.find(
    ([key]) => String(key).trim().toUpperCase() === APP_TYPE_QUERY_PARAM
  )

  return matchingEntry?.[1] || null
}

const resolveAppTypeFromQuery = (query = {}) => normalizeAppType(getAppTypeQueryValue(query))
const resolveAppTypeFromHostEnv = () =>
  normalizeAppType(import.meta.env.VITE_APP_NAME || import.meta.env.VITE_APP_CODE)

const appTypeStorage = useStorage(APP_TYPE_STORAGE_KEY, DEFAULT_APP_TYPE, localStorage)
// Host app builds compile the current variant into env vars. Prefer that over stale
// shared auth storage so guardian/pets do not silently inherit a previous kids session.
const initialAppType = resolveAppType(resolveAppTypeFromHostEnv() || appTypeStorage.value)
appTypeStorage.value = initialAppType

const _appType = ref(initialAppType)
const appType = computed(() => _appType.value)
const appHeader = computed(() => APPS_HEADERS[_appType.value] || APPS_HEADERS[DEFAULT_APP_TYPE])
const isKids = computed(() => _appType.value === APP_TYPES.KIDS)
const isGuardian = computed(() => _appType.value === APP_TYPES.GUARDIAN)
const isPets = computed(() => _appType.value === APP_TYPES.PETS)

const setAppType = (type) => {
  const normalizedType = resolveAppType(type)
  appTypeStorage.value = normalizedType
  _appType.value = normalizedType
}

const getRedirectUrlFromCurrentPublicHost = (type) => {
  if (typeof window === 'undefined') return null

  const hostnameMatch = window.location.hostname.match(/^auth\.([^.]+)\.findee\.localhost$/i)
  if (!hostnameMatch) return null

  const [, profileLabel] = hostnameMatch
  const protocol = window.location.protocol || 'http:'
  const portSuffix = window.location.port ? `:${window.location.port}` : ''
  const targetHost = `${String(type || DEFAULT_APP_TYPE).toLowerCase()}.${profileLabel}.findee.localhost`

  return `${protocol}//${targetHost}${portSuffix}`
}

const getRedirectUrl = () =>
  getRedirectUrlFromCurrentPublicHost(appType.value) || REDIRECT_URLS[appType.value]

export {
  APP_TYPES,
  APPS_HEADERS,
  DEFAULT_APP_TYPE,
  APP_TYPE_QUERY_PARAM,
  normalizeAppType,
  resolveAppTypeFromQuery,
  appType,
  isKids,
  isGuardian,
  isPets,
  appHeader,
  setAppType,
  getRedirectUrl
}
